import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import {
  Category, Subcategory, Sector,
  SUBCATEGORY_LABELS, SECTOR_LABELS,
  SUBCATEGORIES_BY_CATEGORY,
} from '../data/products';
import { Lang } from '../data/translations';
import { FilterOptions, productsApi,  } from '../api/products';

export interface FilterState {
  categories: Category[];
  subcategories: Subcategory[];
  sectors: Sector[];
  brands: string[];
  norms: string[];
  priceMin: string;
  priceMax: string;
  stockOnly: boolean;
  purchaseMode: 'all' | 'direct' | 'quote';
}

export const INITIAL_FILTERS: FilterState = {
  categories: [],
  subcategories: [],
  sectors: [],
  brands: [],
  norms: [],
  priceMin: '',
  priceMax: '',
  stockOnly: false,
  purchaseMode: 'all',
};

export function countActiveFilters(f: FilterState): number {
  return (
    f.categories.length +
    f.subcategories.length +
    f.sectors.length +
    f.brands.length +
    f.norms.length +
    (f.priceMin ? 1 : 0) +
    (f.priceMax ? 1 : 0) +
    (f.stockOnly ? 1 : 0) +
    (f.purchaseMode !== 'all' ? 1 : 0)
  );
}

interface SectionProps {
  title: string;
  count?: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function Section({ title, count, defaultOpen = false, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between py-3 px-1 text-left hover:bg-gray-50/50 rounded transition-colors group"
      >
        <span className="text-xs font-semibold text-gray-700 uppercase tracking-widest group-hover:text-gray-900 flex items-center gap-2">
          {title}
          {count !== undefined && count > 0 && (
            <span className="bg-lodiss-red text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full leading-none">
              {count}
            </span>
          )}
        </span>
        {open ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
      </button>
      {open && <div className="pb-3 px-0.5">{children}</div>}
    </div>
  );
}

interface CheckboxItemProps {
  label: string;
  checked: boolean;
  onChange: () => void;
  count?: number;
}

function CheckboxItem({ label, checked, onChange, count }: CheckboxItemProps) {
  return (
    <label className="flex items-center justify-between gap-2 py-1 cursor-pointer group">
      <div className="flex items-center gap-2.5">
        <div
          onClick={onChange}
          className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors cursor-pointer ${
            checked ? 'bg-lodiss-red border-lodiss-red' : 'border-gray-300 bg-white group-hover:border-gray-400'
          }`}
        >
          {checked && (
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 10" className="w-2.5 h-2.5 fill-white">
              <path d="M1 5l3.5 3.5L11 1" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
        <span className={`text-xs transition-colors ${checked ? 'text-gray-900 font-medium' : 'text-gray-600 group-hover:text-gray-800'}`}>
          {label}
        </span>
      </div>
      {count !== undefined && count > 0 && (
        <span className="text-[10px] text-gray-400 flex-shrink-0">{count}</span>
      )}
    </label>
  );
}

interface Props {
  filters: FilterState;
  onChange: (f: FilterState) => void;
  lang: Lang;
  productCount: number;
}

export default function FilterPanel({ filters, onChange, lang }: Props) {
  const [filterOptions, setFilterOptions] = useState<any | null>(null);
  const [dynamicCategories, setDynamicCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      productsApi.getFiltersMeta(),
      productsApi.getCategories()
    ])
      .then(([meta, catsResponse]) => {
        setFilterOptions(meta);
        setDynamicCategories(catsResponse.categories || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const set = (patch: Partial<FilterState>) => onChange({ ...filters, ...patch });

  const toggle = <T extends string>(arr: T[], val: T): T[] =>
    arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val];

  const activeCats = filters.categories.length > 0 
    ? dynamicCategories.filter(c => filters.categories.includes(c.slug as Category)) 
    : dynamicCategories;
  const visibleSubcats = activeCats.flatMap(c => c.subcategories || []);

  if (loading) {
    return (
      <div className="p-4 text-center text-sm text-gray-400">
        {lang === 'fr' ? 'Chargement...' : 'Loading...'}
      </div>
    );
  }

  const categoryCounts = filterOptions ? filterOptions.category_counts : {};
  const sectorCounts = filterOptions ? filterOptions.sector_counts : {};
  const brandCounts: Record<string, number> = {};
  if (filterOptions) {
    filterOptions.brands.forEach(b => brandCounts[b] = 1);
  }

  return (
    <div className="text-sm">
      <Section
        title={lang === 'fr' ? 'Univers' : 'Universe'}
        count={filters.categories.length}
        defaultOpen
      >
        <div className="space-y-0.5">
          {dynamicCategories.map(c => (
            <CheckboxItem
              key={c.slug}
              label={lang === 'fr' ? c.name_fr : c.name_en}
              checked={filters.categories.includes(c.slug as Category)}
              onChange={() => {
                const newCats = toggle(filters.categories, c.slug as Category);
                const validSubs = dynamicCategories
                  .filter(cat => newCats.includes(cat.slug as Category))
                  .flatMap(cat => (cat.subcategories || []).map((s: any) => s.slug));
                set({
                  categories: newCats,
                  subcategories: filters.subcategories.filter(s => validSubs.includes(s)),
                });
              }}
              count={categoryCounts?.[c.slug] || 0}
            />
          ))}
        </div>
      </Section>

      {/* Sous-catégorie */}
      <Section
        title={lang === 'fr' ? 'Sous-catégorie' : 'Subcategory'}
        count={filters.subcategories.length}
        defaultOpen={filters.categories.length === 1}
      >
        <div className="space-y-0.5">
          {visibleSubcats.map(sub => (
            <CheckboxItem
              key={sub.slug}
              label={lang === 'fr' ? sub.name_fr : sub.name_en}
              checked={filters.subcategories.includes(sub.slug as Subcategory)}
              onChange={() => set({ subcategories: toggle(filters.subcategories, sub.slug as Subcategory) })}
              count={filterOptions?.subcategory_counts?.[sub.slug] || 0}
            />
          ))}
        </div>
      </Section>

      {/* Secteur */}
      {filterOptions?.sectors && filterOptions.sectors.length > 0 && (
        <Section
          title={lang === 'fr' ? 'Secteur d\'activité' : 'Industry sector'}
          count={filters.sectors.length}
          defaultOpen
        >
          <div className="space-y-0.5">
            {filterOptions.sectors.map((s: any) => (
              <CheckboxItem
                key={s.slug || s.id}
                label={lang === 'fr' && s.name_fr ? s.name_fr : (s.name_en || s.label || s.slug || String(s.id))}
                checked={filters.sectors.includes(s.slug || String(s.id))}
                onChange={() => set({ sectors: toggle(filters.sectors, s.slug || String(s.id)) })}
                count={sectorCounts?.[s.slug || s.id] || 0}
              />
            ))}
          </div>
        </Section>
      )}

      {/* Marque */}
      <Section
        title={lang === 'fr' ? 'Marque' : 'Brand'}
        count={filters.brands.length}
      >
        <div className="space-y-0.5">
          {filterOptions?.brands.map(b => (
            <CheckboxItem
              key={b}
              label={b}
              checked={filters.brands.includes(b)}
              onChange={() => set({ brands: toggle(filters.brands, b) })}
              count={brandCounts[b]}
            />
          ))}
        </div>
      </Section>

      {/* Prix */}
      <Section
        title={lang === 'fr' ? 'Prix unitaire (DT HT)' : 'Unit price (DT excl. VAT)'}
        count={(filters.priceMin ? 1 : 0) + (filters.priceMax ? 1 : 0)}
      >
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <label className="block text-[10px] text-gray-400 mb-1">{lang === 'fr' ? 'Min' : 'Min'}</label>
            <input
              type="number"
              placeholder="0"
              value={filters.priceMin}
              onChange={e => set({ priceMin: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-lodiss-red"
            />
          </div>
          <span className="text-gray-400 text-xs mt-4">—</span>
          <div className="flex-1">
            <label className="block text-[10px] text-gray-400 mb-1">{lang === 'fr' ? 'Max' : 'Max'}</label>
            <input
              type="number"
              placeholder="999"
              value={filters.priceMax}
              onChange={e => set({ priceMax: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-lodiss-red"
            />
          </div>
        </div>
      </Section>

      {/* Disponibilité */}
      <Section
        title={lang === 'fr' ? 'Disponibilité' : 'Availability'}
        count={filters.stockOnly ? 1 : 0}
      >
        <CheckboxItem
          label={lang === 'fr' ? 'En stock uniquement' : 'In stock only'}
          checked={filters.stockOnly}
          onChange={() => set({ stockOnly: !filters.stockOnly })}
        />
      </Section>

      {/* Mode achat */}
      <Section
        title={lang === 'fr' ? 'Mode d\'achat' : 'Purchase mode'}
        count={filters.purchaseMode !== 'all' ? 1 : 0}
      >
        <div className="space-y-0.5">
          {[
            { val: 'all', label: lang === 'fr' ? 'Tous les produits' : 'All products' },
            { val: 'direct', label: lang === 'fr' ? 'Achat direct disponible' : 'Direct purchase available' },
            { val: 'quote', label: lang === 'fr' ? 'Sur devis volume' : 'Volume quote required' },
          ].map(opt => (
            <label key={opt.val} className="flex items-center gap-2.5 py-1 cursor-pointer group">
              <div
                onClick={() => set({ purchaseMode: opt.val as FilterState['purchaseMode'] })}
                className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center transition-colors cursor-pointer ${
                  filters.purchaseMode === opt.val
                    ? 'border-lodiss-red bg-white'
                    : 'border-gray-300 bg-white group-hover:border-gray-400'
                }`}
              >
                {filters.purchaseMode === opt.val && (
                  <div className="w-2 h-2 rounded-full bg-lodiss-red" />
                )}
              </div>
              <span className={`text-xs transition-colors ${filters.purchaseMode === opt.val ? 'text-gray-900 font-medium' : 'text-gray-600 group-hover:text-gray-800'}`}>
                {opt.label}
              </span>
            </label>
          ))}
        </div>
      </Section>

      {/* Active chips summary (compact) */}
      {countActiveFilters(filters) > 0 && (
        <div className="pt-3 mt-1">
          <button
            onClick={() => onChange(INITIAL_FILTERS)}
            className="w-full flex items-center justify-center gap-1.5 py-2 px-3 text-xs font-semibold text-lodiss-red border border-lodiss-red/30 rounded-lg hover:bg-red-50 transition-colors"
          >
            <X size={12} />
            {lang === 'fr' ? 'Effacer tous les filtres' : 'Clear all filters'}
          </button>
        </div>
      )}
    </div>
  );
}
